import React, { useState, useMemo } from 'react'
import { useActasCorte } from './useActasCorte'
import { imprimirActaCortePdf } from './exportarActaCortePdf'
import type { ProyectoPlan } from '../tipos'
import type { EstadoActaCorte, ActaCorteObra } from './tipos'
import styles from './ModalActasCorte.module.css'

interface ModalActasCorteProps {
  abierto: boolean
  proyecto: ProyectoPlan
  alCerrar: () => void
}

const ESTADOS_INFO: Record<EstadoActaCorte, { label: string; color: string; bg: string }> = {
  borrador: { label: 'Borrador', color: '#64748b', bg: '#f1f5f9' },
  radicada: { label: 'Radicada', color: '#d97706', bg: '#fffbeb' },
  aprobada: { label: 'Aprobada', color: '#2563eb', bg: '#eff6ff' },
  cobrada: { label: 'Cobrada en Caja', color: '#059669', bg: '#ecfdf5' },
  anulada: { label: 'Anulada', color: '#dc2626', bg: '#fef2f2' },
}

export function ModalActasCorte({ abierto, proyecto, alCerrar }: ModalActasCorteProps) {
  const {
    actas,
    generarActaCorte,
    actualizarEstadoActa,
    eliminarActa,
  } = useActasCorte(proyecto.id)

  const [mostrarCrear, setMostrarCrear] = useState(false)
  const [residenteNombre, setResidenteNombre] = useState('Ing. Residente MEGA')
  const [interventorNombre, setInterventorNombre] = useState('')
  const [fechaCorte, setFechaCorte] = useState(() => new Date().toISOString().split('T')[0])
  const [notas, setNotas] = useState('')
  const [generando, setGenerando] = useState(false)

  const formatearCOP = (val: number) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0,
    }).format(val || 0)

  // Métricas financieras de cartera y cobro de la obra (Tarea 2.8)
  const metricasCartera = useMemo(() => {
    const totalContratado = proyecto.presupuesto || proyecto.tope_contrato || 0
    let totalActasEmitidas = 0
    let totalCobradoEnCaja = 0
    let totalCarteraPendiente = 0

    actas.forEach((a) => {
      if (a.estado !== 'anulada') {
        totalActasEmitidas += a.valor_total
        if (a.estado === 'cobrada') {
          totalCobradoEnCaja += a.valor_total
        } else if (a.estado === 'radicada' || a.estado === 'aprobada') {
          totalCarteraPendiente += a.valor_total
        }
      }
    })

    return {
      totalContratado,
      totalActasEmitidas,
      totalCobradoEnCaja,
      totalCarteraPendiente,
    }
  }, [proyecto, actas])

  if (!abierto) return null

  const handleCrearActa = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setGenerando(true)
      await generarActaCorte(proyecto, {
        residenteNombre: residenteNombre.trim() || 'Ing. Residente',
        interventorNombre: interventorNombre.trim(),
        fechaCorte,
        notas: notas.trim(),
      })
      setMostrarCrear(false)
      setNotas('')
    } catch (err) {
      console.error('Error al generar acta:', err)
      alert('Error generando acta de corte.')
    } finally {
      setGenerando(false)
    }
  }

  const handleCambiarEstado = (acta: ActaCorteObra, nuevoEstado: EstadoActaCorte) => {
    if (nuevoEstado === 'radicada') {
      const fechaRad = prompt('Fecha de radicación formal ante el cliente (YYYY-MM-DD):', new Date().toISOString().split('T')[0])
      if (!fechaRad) return
      actualizarEstadoActa(acta.id, 'radicada', { fechaRadicacion: fechaRad })
    } else if (nuevoEstado === 'cobrada') {
      const fechaCob = prompt('Fecha en que ingresó el dinero efectivo a caja (YYYY-MM-DD):', new Date().toISOString().split('T')[0])
      if (!fechaCob) return
      actualizarEstadoActa(acta.id, 'cobrada', { fechaCobro: fechaCob })
    } else {
      actualizarEstadoActa(acta.id, nuevoEstado)
    }
  }

  return (
    <div className={styles.overlay} onClick={alCerrar}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Encabezado */}
        <div className={styles.header}>
          <div>
            <div className={styles.badgeTop}>SUSTENTO DE COBRO CONTRACTUAL</div>
            <h3 className={styles.titulo}>Actas de Corte y Cartera de Obra</h3>
            <p className={styles.subtitulo}>{proyecto.nombre} • Cliente: {proyecto.cliente || 'GENERAL'}</p>
          </div>
          <button type="button" className={styles.btnCerrar} onClick={alCerrar}>
            ✕
          </button>
        </div>

        {/* Panel de métricas financieras de cobro (Tarea 2.8) */}
        <div className={styles.kpisContenedor}>
          <div className={styles.kpiTile}>
            <span className={styles.kpiEtiqueta}>Total Contratado</span>
            <span className={styles.kpiValor}>{formatearCOP(metricasCartera.totalContratado)}</span>
          </div>
          <div className={styles.kpiTile}>
            <span className={styles.kpiEtiqueta}>Total Medido en Actas</span>
            <span className={styles.kpiValor}>{formatearCOP(metricasCartera.totalActasEmitidas)}</span>
          </div>
          <div className={`${styles.kpiTile} ${styles.kpiCaja}`}>
            <span className={styles.kpiEtiqueta}>Recaudado en Caja</span>
            <span className={styles.kpiValorCaja}>{formatearCOP(metricasCartera.totalCobradoEnCaja)}</span>
          </div>
          <div className={`${styles.kpiTile} ${styles.kpiCartera}`}>
            <span className={styles.kpiEtiqueta}>Cartera Pendiente por Cobrar</span>
            <span className={styles.kpiValorCartera}>{formatearCOP(metricasCartera.totalCarteraPendiente)}</span>
          </div>
        </div>

        {/* Barra de acción */}
        <div className={styles.barraAcciones}>
          <button
            type="button"
            className={styles.btnNuevoCorte}
            onClick={() => setMostrarCrear(!mostrarCrear)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <line x1="9" y1="15" x2="15" y2="15" />
            </svg>
            <span>{mostrarCrear ? 'Cerrar Formulario' : '+ Generar Nueva Acta de Corte'}</span>
          </button>
          <span className={styles.infoActas}>
            {actas.length} actas emitidas para este contrato
          </span>
        </div>

        {/* Formulario Nueva Acta */}
        {mostrarCrear && (
          <form className={styles.formCrear} onSubmit={handleCrearActa}>
            <div className={styles.formGrid}>
              <div>
                <label className={styles.label}>Fecha de Corte *</label>
                <input
                  type="date"
                  required
                  value={fechaCorte}
                  onChange={(e) => setFechaCorte(e.target.value)}
                  className={styles.inputSimple}
                />
              </div>
              <div>
                <label className={styles.label}>Ing. Residente MEGA *</label>
                <input
                  type="text"
                  required
                  value={residenteNombre}
                  onChange={(e) => setResidenteNombre(e.target.value)}
                  className={styles.inputSimple}
                />
              </div>
              <div>
                <label className={styles.label}>Interventoría / Supervisión</label>
                <input
                  type="text"
                  placeholder="Nombre interventor o inspector..."
                  value={interventorNombre}
                  onChange={(e) => setInterventorNombre(e.target.value)}
                  className={styles.inputSimple}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className={styles.label}>Notas u Observaciones del Acta</label>
                <input
                  type="text"
                  placeholder="Notas de avance, condiciones de cobro, aprobación de interventoría..."
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  className={styles.inputSimple}
                />
              </div>
            </div>

            <div className={styles.formFooter}>
              <button type="button" className={styles.btnCancelar} onClick={() => setMostrarCrear(false)}>
                Cancelar
              </button>
              <button type="submit" className={styles.btnConfirmar} disabled={generando}>
                {generando ? 'Compilando Actividades...' : 'Crear Acta y Congelar Corte'}
              </button>
            </div>
          </form>
        )}

        {/* Listado de Actas */}
        <div className={styles.tablaWrapper}>
          {actas.length === 0 ? (
            <div className={styles.sinActas}>
              No hay actas de corte generadas para esta obra. Cuando el residente registre avances físicos, genere un acta para sustentar el cobro formal ante el cliente.
            </div>
          ) : (
            <table className={styles.tabla}>
              <thead>
                <tr>
                  <th style={{ width: '130px' }}>Consecutivo</th>
                  <th style={{ width: '80px', textAlign: 'center' }}>Corte</th>
                  <th style={{ width: '100px' }}>Fecha</th>
                  <th style={{ width: '130px', textAlign: 'right' }}>Costo Directo</th>
                  <th style={{ width: '140px', textAlign: 'right' }}>Total con AIU</th>
                  <th style={{ width: '130px', textAlign: 'center' }}>Estado</th>
                  <th style={{ width: '140px' }}>Fecha Cobro</th>
                  <th style={{ width: '120px', textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {actas.map((acta) => {
                  const estInfo = ESTADOS_INFO[acta.estado] || { label: acta.estado, color: '#475569', bg: '#f1f5f9' }

                  return (
                    <tr key={acta.id}>
                      <td className={styles.consecutivoTd}>{acta.consecutivo}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700 }}>#{acta.numero_corte}</td>
                      <td style={{ color: '#64748b' }}>{acta.fecha_corte}</td>
                      <td style={{ textAlign: 'right', color: '#475569' }}>
                        {formatearCOP(acta.valor_costo_directo)}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 800, color: '#0f172a' }}>
                        {formatearCOP(acta.valor_total)}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <select
                          value={acta.estado}
                          onChange={(e) => handleCambiarEstado(acta, e.target.value as EstadoActaCorte)}
                          className={styles.selectEstado}
                          style={{ color: estInfo.color, backgroundColor: estInfo.bg }}
                        >
                          <option value="borrador">Borrador</option>
                          <option value="radicada">Radicada</option>
                          <option value="aprobada">Aprobada</option>
                          <option value="cobrada">Cobrada en Caja</option>
                          <option value="anulada">Anulada</option>
                        </select>
                      </td>
                      <td style={{ fontSize: '0.75rem', color: acta.fecha_cobro ? '#059669' : '#94a3b8' }}>
                        {acta.fecha_cobro ? `Cobrado: ${acta.fecha_cobro}` : 'Pendiente de cobro'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div className={styles.accionesTd}>
                          <button
                            type="button"
                            className={styles.btnPdf}
                            onClick={() => imprimirActaCortePdf(acta)}
                            title="Imprimir Acta Oficial en PDF para firma y cobro"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="6 9 6 2 18 2 18 9" />
                              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                              <rect x="6" y="14" width="12" height="8" />
                            </svg>
                            <span>PDF</span>
                          </button>
                          <button
                            type="button"
                            className={styles.btnEliminar}
                            onClick={() => {
                              if (confirm(`¿Eliminar el acta de corte ${acta.consecutivo}?`)) {
                                eliminarActa(acta.id)
                              }
                            }}
                            title="Eliminar acta"
                          >
                            ✕
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button type="button" className={styles.btnCerrarModal} onClick={alCerrar}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

